import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpService } from '@ats/api-sdk';
import { EfpAppConfigService } from '@ats/app-config';
import { ConfigService, FlagsService } from '@ats/service-base';
import { AuthService, User } from '@auth0/auth0-angular';
import { TranslocoService } from '@ngneat/transloco';
import { productFruits } from 'product-fruits';
import { of, Subject } from 'rxjs';
import { catchError, filter, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { PMEUserService } from './core/services/pme-user.service';
import { IAppUser } from './interfaces/user.interface';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly returnUrlKey = 'pme:returnUrl';

  userId!: string;
  userSideId!: string;
  userPerson!: IAppUser;

  buildNumber: string = '';

  /**
   * Container being viewed
   */
  currentRoute!: string;

  featureToggles$ = this.flagsService.featureFlagsForPage('light_dark_mode_switch');

  authError$ = this.auth.error$.pipe(
    filter((error) => !!error),
    tap((_res) => {
      // If error redirect to unguarded component to prevent login loop
      this.router.navigateByUrl('auth_error');
    })
  );

  user$ = this.auth.user$.pipe(
    filter((user) => user !== null && user !== undefined),
    // Set global variables
    tap((user) => {
      this.userId = user?.email as string;
      this.userPerson = {
        id: (user as User)?.['https://advancedtech.com/uuid'] as string,
        firstname: (user as User)?.given_name ?? '',
        lastname: (user as User)?.family_name ?? '',
        fullname: `${(user as User)?.given_name ?? ''} ${(user as User)?.family_name ?? ''}`,
        email: (user as User)?.email ?? '',
        roles: (user as User)?.['https://advancedtech.com/roles'] ?? []
      };
    }),
    // Initialize NewRelic
    tap((user) => {
      // Set custom NewRelic attributes for session traces
      // biome-ignore lint/suspicious/noExplicitAny: Turning off for window to allow build
      (window as any)['newrelic']?.setCustomAttribute('user', user?.email as string);
    }),
    // Initialize Flagsmith
    tap((user) => {
      this.initBulletTrainFeatureToggles(user?.['https://efactorypro.com/id'] as string);
    }),
    // Apply theme
    tap((user) => {
      const userId = user?.email?.toLowerCase();
      // Apply dark/light mode for user
      const theme = localStorage.getItem(`efp-theme-${userId}`) === 'dark' ? 'dark' : 'light';
      localStorage.setItem(`efp-theme-${userId}`, theme);
      document.documentElement.classList.add(`${theme}Mode`);
      this.pmeUserService.currentUser = this.userPerson;
      this.pmeUserService.currentUserChangedSubj.next();

      this.initProductFruits(user as User);
    }),
    catchError(() => {
      this.router.navigate(['/auth_error']);

      return of({} as IAppUser);
    }),
    startWith({
      id: '',
      firstname: '',
      lastname: '',
      fullname: '',
      email: '',
      roles: []
    } as IAppUser)
  );

  private ngUnsubscribe = new Subject<void>();

  constructor(
    public auth: AuthService,
    private router: Router,
    private translate: TranslocoService,
    private appconfig: EfpAppConfigService,
    private httpService: HttpService,
    private flagsService: FlagsService,
    private configService: ConfigService,
    private pmeUserService: PMEUserService
  ) {
    this.httpService.config = {
      apiHost: appconfig.config.uris.aks
    };
  }

  ngOnInit(): void {
    this.configService.setUris(this.appconfig.config.uris);

    this.initTranslationService();

    this.auth.isAuthenticated$
      .pipe(
        filter((isAuthenticated) => isAuthenticated),
        map(() => sessionStorage.getItem(this.returnUrlKey)),
        filter((target): target is string => typeof target === 'string' && target.length > 0),
        tap((target) => {
          sessionStorage.removeItem(this.returnUrlKey);
          if (target !== this.router.url) {
            this.router.navigateByUrl(target);
          }
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map((e: NavigationEnd) => {
          const firstPathSegment = e.url.split('/')[1]?.split('?')[0];

          if (firstPathSegment) {
            this.currentRoute = firstPathSegment;
          }
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();

    this.buildNumber =
      (this.appconfig.config['buildNumber'] as string)?.match(/^(\d*\.\d*\.\d*)|local/)?.[0] ?? '';
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initProductFruits(user: User): void {
    productFruits.init(
      'Fv0sEjXxNSXcmo1A',
      navigator.languages[0].split('-')?.[0].toLowerCase() ?? 'en',
      { username: user.email ?? '' }
    );
  }

  initBulletTrainFeatureToggles(personId: string): void {
    this.flagsService.initializeFlagsmith(
      this.appconfig.config['bulletTrainEnvId'] as string,
      personId
    );
  }

  initTranslationService(): void {
    let lang: string = navigator.language;

    if (lang.toLowerCase().substring(0, 2) === 'en') {
      lang = 'en-US';
    } else if (lang.toLowerCase().substring(0, 2) === 'es') {
      lang = 'es-MX';
    }

    this.translate.setDefaultLang(lang);
  }
}
